import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Field, Spinner } from '../ui.jsx';

const APP_ORIGIN = () => (typeof window !== 'undefined' ? window.location.origin : '');

export function ClaudeConnector({ user, toast }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState('Claude Desktop');
  const [justCreated, setJustCreated] = useState(null);

  const load = () => api('/api/mcp/keys').then(r => setKeys(r.keys || [])).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const r = await api('/api/mcp/keys', { method:'POST', body:{ label: newLabel || 'Claude Connector' }});
      setJustCreated(r);
      setShowCreate(false);
      setNewLabel('Claude Desktop');
      load();
    } catch(e) { toast(e.message,'error'); }
  };
  const revoke = async (id) => {
    if (!confirm('Revoke this key? Any Claude session using it will stop working.')) return;
    try { await api(`/api/mcp/keys/${id}/revoke`, { method:'POST' }); load(); toast('Revoked'); }
    catch(e) { toast(e.message,'error'); }
  };
  const copy = (text) => { try { navigator.clipboard.writeText(text); toast('Copied'); } catch {} };

  const openapiUrl = `${APP_ORIGIN()}/api/mcp/openapi.json`;
  const activeKeys = keys.filter(k => !k.revoked_at);

  return <div style={{ padding:24, maxWidth:980 }}>
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18, gap:14 }}>
      <div>
        <h1 style={{ margin:0, fontSize:26, fontWeight:800, display:'flex', alignItems:'center', gap:10 }}>
          🧠 Claude Connector
          <Badge c={G.purple} bg={G.purpleDim}>NEW</Badge>
        </h1>
        <div style={{ fontSize:14, color:G.textMuted, marginTop:6, lineHeight:1.6 }}>
          Use TradeOps from inside <strong>Claude.ai</strong> or <strong>Claude Desktop</strong>. Ask Claude to look up jobs,
          send a customer SMS, schedule appointments, fire AI calls — it'll do the work and link you back to the app for the
          high-value moments (live AI transcripts, payment confirmation, full estimates).
        </div>
      </div>
      <Badge c={activeKeys.length?G.green:G.textMuted} bg={(activeKeys.length?G.green:G.textMuted)+'22'} style={{ flexShrink:0 }}>
        {activeKeys.length ? `${activeKeys.length} active key${activeKeys.length>1?'s':''}` : 'Not connected'}
      </Badge>
    </div>

    {/* SETUP WALKTHROUGH */}
    <div style={{ ...styles.card, padding:20, marginBottom:18 }}>
      <h2 style={{ margin:'0 0 14px 0', fontSize:18 }}>📖 Setup (60 seconds)</h2>

      <Step n={1} title="Generate an API key">
        <div style={{ marginBottom:8 }}>Click below — you'll get a token starting with <code style={inline}>tk_…</code>. Save it; we only show it once.</div>
        {!showCreate && !justCreated && <Btn v="primary" onClick={()=>setShowCreate(true)}>+ Generate API Key</Btn>}
        {showCreate && (
          <div style={{ padding:14, background:G.input, borderRadius:10 }}>
            <Field label="Key label (where you'll use it)">
              <Input value={newLabel} onChange={e=>setNewLabel(e.target.value)} placeholder="Claude Desktop / Claude.ai / Phone"/>
            </Field>
            <div style={{ display:'flex', gap:8, marginTop:10, justifyContent:'flex-end' }}>
              <Btn onClick={()=>setShowCreate(false)}>Cancel</Btn>
              <Btn v="primary" onClick={create}>Generate</Btn>
            </div>
          </div>
        )}
        {justCreated && (
          <div style={{ padding:14, background:G.greenLight, border:`1px solid ${G.green}66`, borderRadius:10 }}>
            <strong style={{ color:G.green }}>🔑 Your API key (save this now)</strong>
            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <code style={{ flex:1, padding:'10px 12px', background:G.bg, borderRadius:6, fontSize:13, overflow:'auto', wordBreak:'break-all' }}>{justCreated.plaintext}</code>
              <Btn sz="sm" onClick={()=>copy(justCreated.plaintext)}>📋 Copy</Btn>
            </div>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:8 }}>{justCreated.note}</div>
            <Btn sz="sm" v="ghost" onClick={()=>setJustCreated(null)} style={{ marginTop:10 }}>Hide (I saved it)</Btn>
          </div>
        )}
      </Step>

      <Step n={2} title="Open Claude's Connector settings">
        <div>
          <Btn v="primary" sz="sm" onClick={()=>{
            // Smoother UX: open Claude.ai connectors in a new tab (we can't pre-fill via URL params yet,
            // but at least we save them a click). The plaintext key + OpenAPI URL are still in clipboard from step 1+3.
            window.open('https://claude.ai/settings/connectors','_blank','noopener,noreferrer');
          }}>🚀 Open Claude.ai Connectors →</Btn>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:6 }}>Or in Claude Desktop: Settings → Connectors → Add custom connector</div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>(Anthropic doesn't yet support 1-click connector handoff — but it's coming. For now: Open → Add custom → paste the values from step 3.)</div>
        </div>
      </Step>

      <Step n={3} title="Paste the connection details">
        <div style={{ display:'grid', gap:10 }}>
          <CopyRow label="OpenAPI URL" value={openapiUrl} onCopy={copy}/>
          <CopyRow label="Auth Type" value="Bearer Token" onCopy={copy}/>
          <CopyRow label="Bearer Token" value={'tk_… (the key from step 1)'} onCopy={()=>{}} muted/>
        </div>
      </Step>

      <Step n={4} title="Try it" last>
        <div>Ask Claude:</div>
        <div style={{ marginTop:10, display:'grid', gap:6 }}>
          {[
            '"What\'s on my TradeOps schedule today?"',
            '"Show me overdue invoices over $500"',
            '"Text Sarah Thompson asking if Tuesday at 2 still works for her"',
            '"Schedule Mike for the Smith install Thursday at 10am"',
            '"Have the AI call every overdue invoice"',
          ].map((q, i) => (
            <div key={i} style={{ padding:'10px 14px', background:G.input, borderRadius:8, fontSize:13 }}>{q}</div>
          ))}
        </div>
      </Step>
    </div>

    {/* WHAT CLAUDE CAN DO */}
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:18 }}>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 12px 0', fontSize:15, color:G.green }}>👀 Claude can read</h3>
        <ul style={listStyle}>
          <li>Today's schedule + tomorrow's route</li>
          <li>Any contact's full profile + LTV + churn risk</li>
          <li>Overdue invoices (filterable by $$ threshold)</li>
          <li>Team — who's working what today</li>
          <li>Cross-account winning templates (insights)</li>
          <li>Search contacts by name/phone/email</li>
        </ul>
      </div>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 12px 0', fontSize:15, color:G.purple }}>✋ Claude can do</h3>
        <ul style={listStyle}>
          <li>Send manual SMS from your business number</li>
          <li>Schedule a job for any contact</li>
          <li>Trigger an AI voice call (transcript opens in TradeOps)</li>
          <li>Draft an estimate stub (you finish the AI tiers in-app)</li>
        </ul>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:10, lineHeight:1.5 }}>
          ✨ Each Claude action returns a deep-link back to TradeOps so you can review, approve, or extend.
        </div>
      </div>
    </div>

    {/* ACTIVE KEYS */}
    <div style={styles.card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <h3 style={{ margin:0, fontSize:15 }}>🔑 Your API keys</h3>
        {keys.length > 0 && <Btn sz="sm" onClick={()=>setShowCreate(true)}>+ New Key</Btn>}
      </div>
      {loading ? <Spinner/> : keys.length === 0 ? (
        <div style={{ padding:20, textAlign:'center', color:G.textMuted, fontSize:13 }}>No keys yet. Generate one above to connect Claude.</div>
      ) : (
        <table style={{ width:'100%', fontSize:13 }}>
          <thead><tr>{['Label','Created','Last Used',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
          <tbody>{keys.map(k => (
            <tr key={k.id}>
              <td style={styles.td}>
                {k.label}
                {k.revoked_at && <Badge c={G.red} bg={G.red+'22'} style={{ marginLeft:8 }}>revoked</Badge>}
              </td>
              <td style={styles.td}>{new Date(k.created_at).toLocaleDateString()}</td>
              <td style={styles.td}>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : <span style={{ color:G.textMuted }}>never</span>}</td>
              <td style={styles.td}>{!k.revoked_at && <Btn sz="xs" v="danger" onClick={()=>revoke(k.id)}>Revoke</Btn>}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>

    {/* SECURITY NOTE */}
    <div style={{ ...styles.card, marginTop:18, padding:14, background:G.input, borderColor:G.cyan+'33' }}>
      <div style={{ fontSize:13, lineHeight:1.6 }}>
        🔒 <strong>Security:</strong> Each key is scoped to your account only. Other sub-accounts can never see your data through your token. Revoke any time — Claude immediately stops working with that key.
      </div>
    </div>
  </div>;
}

function Step({ n, title, children, last }) {
  return <div style={{ display:'flex', gap:14, paddingBottom: last?0:18, marginBottom: last?0:18, borderBottom: last?'none':`1px solid ${G.cardBorder}` }}>
    <div style={{
      width:32, height:32, borderRadius:99, background:G.purple+'22', color:G.purple,
      display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, flexShrink:0,
    }}>{n}</div>
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>{title}</div>
      <div>{children}</div>
    </div>
  </div>;
}

function CopyRow({ label, value, onCopy, muted }) {
  return <div style={{ display:'flex', gap:8, alignItems:'center' }}>
    <div style={{ width:130, fontSize:12, color:G.textMuted, fontWeight:600 }}>{label}</div>
    <code style={{ flex:1, padding:'8px 10px', background:G.bg, borderRadius:6, fontSize:12, color: muted?G.textMuted:G.text, wordBreak:'break-all' }}>{value}</code>
    {!muted && <Btn sz="xs" onClick={()=>onCopy(value)}>📋</Btn>}
  </div>;
}

const inline = { background:'rgba(139,92,246,.14)', padding:'2px 6px', borderRadius:4, fontSize:12 };
const listStyle = { margin:0, paddingLeft:18, lineHeight:1.8, fontSize:13 };
